import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { jwtDecode } from 'jwt-decode';

interface StatisticsData {
    usersCount: number;
    addressesCount: number;
    topAddressesByPincode: { pinCode: string; addressCount: number }[];
}

interface CustomJwtPayload {
    RoleId: string;
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate('/');
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                if (decodedToken.RoleId !== "1") {
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate('/');
                }
            } catch (error) {
                toast.error("Invalid token. Please log in again.");
                navigate('/');
            }
        }
    }, [navigate]);

    const formatDate = (date: Date | null) => {
        if (!date) return "";
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    const fetchStatistics = async (fromDate: string, toDate: string) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No auth token found');

            const response = await fetch(`https://pamsapi.vofoxsolutions.com/api/Admin/GetStatisticsOfUsersAndAddressAsync?fromDate=${fromDate}&toDate=${toDate}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error(`Failed to fetch statistics: ${response.status}`);

            const data: StatisticsData = await response.json();
            setStatisticsData(data);
            if (data.usersCount === 0) toast.info("No users were registered within the selected date range.");
        } catch (error) {
            // toast.error("An error occurred while fetching statistics.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFetchData = () => {
        if (!startDate || !endDate) {
            if (!toast.isActive("select-dates")) {
                toast.info("Please select both start and end date.", { toastId: "select-dates" });
            }
            return;
        }
        if (startDate > endDate) {
            if (!toast.isActive("invalid-dates")) {
                toast.error("Start date must be earlier than or equal to end date.", { toastId: "invalid-dates" });
            }
            return;
        }
        fetchStatistics(formatDate(startDate), formatDate(endDate));
    };

    return (
        <div className="h-screen overflow-hidden bg-magnolia">
            <div className="flex flex-col h-screen">
                <header className="flex items-center justify-between px-6 py-3 bg-white">
                    <h1 className="text-lg font-semibold text-gray-700">Dashboard</h1>
                </header>
                <main className="flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="container px-6 py-6 mx-auto">
                        <h4 className="text-base sm:text-lg text-dark font-semibold">User and Address Statistics</h4>
                        <div className="flex gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        setStartDate(date);
                                        setEndDate(null);
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={new Date()}
                                    dateFormat="MM-dd-yyyy"
                                    className="border rounded p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">End Date</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    maxDate={new Date()}
                                    dateFormat="MM-dd-yyyy"
                                    className="border rounded p-2"
                                    disabled={!startDate}
                                />
                            </div>
                            <button
                                onClick={handleFetchData}
                                className="bg-indigo-600 text-white px-4 h-10 rounded-md place-self-end"
                            >
                                Fetch Data
                            </button>
                        </div>
                        {isLoading && <p className="mt-4 text-gray-500">Loading data...</p>}
                        {statisticsData && statisticsData.topAddressesByPincode && (
                            <div className="mt-6">
                            <h4 className="text-lg font-semibold">Statistics (From {formatDate(startDate)} to {formatDate(endDate)})</h4>
                            
                            {/* First Row: Total Users & Total Addresses */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                                <div className="p-6 bg-white rounded-lg shadow-md">
                                    <h5 className="text-sm font-semibold text-gray-600">Total Users</h5>
                                    <p className="text-2xl font-bold text-indigo-600">{statisticsData.usersCount}</p>
                                </div>
                                
                                <div className="p-6 bg-white rounded-lg shadow-md">
                                    <h5 className="text-sm font-semibold text-gray-600">Total Addresses</h5>
                                    <p className="text-2xl font-bold text-indigo-600">{statisticsData.addressesCount}</p>
                                </div>
                            </div>
                        
                            {/* Second Row: Top Pincodes (Expanding Dynamically) */}
                            <div className="mt-6">
                                <div className="p-6 bg-white rounded-lg shadow-md">
                                    <h5 className="text-sm font-semibold text-gray-600">Top Pincodes</h5>
                                    <ul className="text-sm font-bold text-indigo-600 mt-2 max-h-[300px] overflow-y-auto">
                                        {statisticsData.topAddressesByPincode.map((pincode, index) => (
                                            <li key={index}>{pincode.pinCode} - {pincode.addressCount} addresses</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        

                        )}
                    </div>
                </main>
                <ToastContainer />
            </div>
        </div>
    );
};

export default Dashboard;
